import type { TFunction } from "i18next";
import React from "react";
import { Typography, type SxProps, type Theme, type TypographyProps } from "@mui/material";
import type { DayWorkStatus } from "../types/dashboard2-work-status.types";
import { dashboard2StatusDetailWrapSx } from "../dashboard2-status.styles";
import {
  statusPillExtraBookingsShortLine,
  statusPillOfficeInventoryLine,
  statusPillOfficeTimeLine,
  statusPillSecondaryLine
} from "../utils/dashboard2-status-display.utils";

type Dashboard2StatusInventoryTimeLinesProps = {
  status: DayWorkStatus;
  inventoryVariant?: TypographyProps["variant"];
  timeVariant?: TypographyProps["variant"];
  extraVariant?: TypographyProps["variant"];
  inventorySx?: SxProps<Theme>;
  timeSx?: SxProps<Theme>;
  extraSx?: SxProps<Theme>;
  includeExtraBookingsLine?: boolean;
  t?: TFunction;
  inventoryTestId?: string;
  timeTestId?: string;
};

export const Dashboard2StatusInventoryTimeLines: React.FC<
  Dashboard2StatusInventoryTimeLinesProps
> = ({
  status,
  inventoryVariant = "body2",
  timeVariant = "caption",
  extraVariant = "caption",
  inventorySx,
  timeSx,
  extraSx,
  includeExtraBookingsLine = false,
  t,
  inventoryTestId,
  timeTestId
}) => {
  const inventory = statusPillOfficeInventoryLine(status);
  const time = statusPillOfficeTimeLine(status, t);
  const extraLine =
    includeExtraBookingsLine && t ? statusPillExtraBookingsShortLine(status, t) : undefined;
  const fallbackSecondary =
    status.kind === "office" ? undefined : statusPillSecondaryLine(status, t);

  const detailLineSx = (customSx?: SxProps<Theme>): SxProps<Theme> => ({
    ...(dashboard2StatusDetailWrapSx as object),
    ...(customSx as object)
  });

  return (
    <>
      {inventory && (
        <Typography
          variant={inventoryVariant}
          sx={detailLineSx(inventorySx)}
          data-testid={inventoryTestId}
        >
          {inventory}
        </Typography>
      )}
      {time && (
        <Typography variant={timeVariant} sx={detailLineSx(timeSx)} data-testid={timeTestId}>
          {time}
        </Typography>
      )}
      {fallbackSecondary && (
        <Typography variant={inventoryVariant} sx={detailLineSx(inventorySx)}>
          {fallbackSecondary}
        </Typography>
      )}
      {extraLine && (
        <Typography variant={extraVariant} sx={detailLineSx(extraSx)}>
          {extraLine}
        </Typography>
      )}
    </>
  );
};
